/**
 * createAppStore — Zustand store factory with devtools middleware.
 *
 * Supports plain state creators and middleware-wrapped creators (e.g. persist).
 *
 * @example Plain store
 * ```ts
 * export const useAppStore = createAppStore<AppState>('app-store', (set) => ({
 *   count: 0,
 *   increment: () => set((s) => ({ count: s.count + 1 })),
 * }));
 * ```
 *
 * @example With persist middleware
 * ```ts
 * import { persist } from 'zustand/middleware';
 * export const useStore = createAppStore<State>('my-store', persist(
 *   (set) => ({ ... }),
 *   { name: 'my-store', partialize: (s) => ({ key: s.key }) }
 * ));
 * ```
 */

import { create, type StateCreator } from "zustand";
import { devtools } from "zustand/middleware";

export function createAppStore<T>(
  name: string,
  // biome-ignore lint/suspicious/noExplicitAny: StateCreator's 3rd type param (Mcs — applied mutators) must accept ANY additional middleware chain (e.g. persist's own mutator tuple entry) beyond devtools; `unknown` breaks assignability for those wrapped initializers.
  initializer: StateCreator<T, [["zustand/devtools", never]], any>
) {
  return create<T>()(devtools(initializer, { name }));
}
